import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { authAction } from "@redux/modules";
import showErrMessage from "@components/ShowErrMessage";
import "./styles.scss";
import "@assets/styles/public.scss";
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { Modal } from 'react-bootstrap';
import InputMask from 'react-input-mask';

// Toastify
import showMessage from "@components/ShowMessage";

// Cookie yardımcı fonksiyonu
const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
};

const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const act = useSelector((store) => store.auth);
  const loginSettings = useSelector((store) => store.auth.loginSettings);

  const [loginType, setLoginType] = useState("username");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [passwordType, setPasswordType] = useState("password");
  const [disabledButton, setDisabledButton] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmMobileNo, setConfirmMobileNo] = useState("");
  const [confirmCode, setConfirmCode] = useState("");
  const [timer, setTimer] = useState(120); // 2 minutes in seconds
  const [timerExpired, setTimerExpired] = useState(false);
  const [timerKey, setTimerKey] = useState(0); // resend ile sayacı yeniden başlatmak için
  const [showAydinlatmaModal, setShowAydinlatmaModal] = useState(false);
  const [showKisiselVerilerModal, setShowKisiselVerilerModal] = useState(false);
  const [showCerezPolitikasiModal, setShowCerezPolitikasiModal] = useState(false);

  useEffect(() => {
    if (showConfirmModal) {
      setTimer(120); // Reset timer to 2 minutes
      setTimerExpired(false);
      const interval = setInterval(() => {
        setTimer((prevTimer) => {
          if (prevTimer <= 1) {
            clearInterval(interval);
            // Süre dolunca modalı kapatmıyoruz; kullanıcı "Tekrar Kod Gönder" ile
            // yeni kod isteyebilsin diye sadece sayacı dolmuş işaretliyoruz.
            setTimerExpired(true);
            return 0;
          }
          return prevTimer - 1;
        });
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [showConfirmModal, timerKey]);

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs < 10 ? "0" : ""}${secs}`;
  };

  useEffect(() => {
    if (!showConfirmModal) {
      setConfirmMobileNo("");
      setConfirmCode("");
    }
  }, [showConfirmModal]);

  // Login başarılı olduğunda yönlendirme için useEffect.
  // Doğrudan kullanıcının hedef sayfasına navigate ederiz.
  // PrivateRoute global gate olarak çalışır:
  //   - Aktif disclaimer + geçerli onay yoksa, hedef sayfa yerine
  //     /sorumluluk-reddi-beyani'ye yönlendirir ve hedef sayfayı sessionStorage'a yazar.
  //   - Onay tamamlandığında Disclaimer sayfası bu hedefe geri yönlendirir.
  useEffect(() => {
    if (act.isAuthenticated && act.authUser) {
      const redirectTimer = setTimeout(() => {
        // Başarılı login sonrası her zaman /home açılır.
        navigate("/home", { replace: true });
      }, 100);

      return () => clearTimeout(redirectTimer);
    }
  }, [act.isAuthenticated, act.authUser, navigate]);

  // Telefon numarasını maskeli gösterir: 0532 *** ** 67
  const maskPhone = (phone) => {
    if (!phone) return "";
    const digits = String(phone).replace(/\D/g, "");
    if (digits.length < 6) return digits;
    return `${digits.slice(0, 4)} *** ** ${digits.slice(-2)}`;
  };

  const handleConfirmCode = (event) => {
    event.preventDefault();
    event.stopPropagation();

    const form = event.currentTarget;

    if (form.checkValidity() !== false) {
      dispatch(authAction.registerVendorConfirm(confirmCode, confirmMobileNo))
        .then(async (e) => {
          if (e.isHttpSuccess) {
            showMessage("Telefon numaranız doğrulandı.");
            setShowConfirmModal(false);

            // Doğrulama başarılı: aynı bilgilerle otomatik giriş denenir.
            try {
              const response = await dispatch(
                authAction.login({ username, password })
              );
              if (response.redirectTo) {
                navigate(response.redirectTo, { replace: true });
              }
              // Normal login başarılı ise yönlendirme useEffect ile yapılacak
            } catch (error) {
              showErrMessage(error.message);
            }
          }
        })
        .catch((err) => {
          if (err.isHttpError) {
            showErrMessage(err.message);
          }
        });
    }
  };

  // Süre dolduğunda / kullanıcı yeniden istediğinde yeni doğrulama kodu gönderir.
  const handleResendCode = () => {
    dispatch(authAction.registerVendorResendCode(confirmMobileNo))
      .then((e) => {
        if (e.isHttpSuccess) {
          showMessage("Yeni doğrulama kodu telefon numaranıza gönderildi.");
          setConfirmCode("");
          setTimerKey((prev) => prev + 1); // sayacı yeniden başlat
        } else {
          showErrMessage("Kod gönderilemedi, lütfen tekrar deneyiniz.");
        }
      })
      .catch(() => {
        showErrMessage("Kod gönderilemedi, lütfen tekrar deneyiniz.");
      });
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setDisabledButton(true);
    setLoading(true);

    // authData get from cookies
    let authData = getCookie(username);

    if (authData) {
      authData = JSON.parse(authData);
    }

    try {
      let response;
      if(authData?.uniqueField === username) {
        response = await dispatch(authAction.login({ username, password, proofKey: authData?.proofKey }));
      } else {
        response = await dispatch(authAction.login({ username, password }));
      }
      
      // Eğer özel yönlendirme gerekiyorsa (şifre değiştirme, 2FA vb.)
      if (response.redirectTo) {
        navigate(response.redirectTo, { replace: true });
      }
      // Normal login başarılı ise yönlendirme useEffect ile yapılacak
    } catch (error) {
      const data = error?.error?.response?.data;

      // Kod 1034: Telefon numarası doğrulanmamış. Backend bu noktada telefona
      // doğrulama kodu (SMS) gönderir; biz de kodu girebileceği modalı açıyoruz.
      if (data?.code === 1034) {
        // mobileNo backend'in döndüğü fields[0] içinde gelir; yoksa telefon ile
        // giriş yapılıyorsa girilen numarayı kullan.
        const mobileNo =
          data?.fields?.[0] || (loginType === "phone" ? username : "");
        setConfirmMobileNo(mobileNo);
        setConfirmCode("");
        setShowConfirmModal(true);
      }

      showErrMessage(error.message);
    } finally {
      setDisabledButton(false);
      setLoading(false);
    }
  };

  const handlePasswordTypeChange = () => {
    setPasswordType(passwordType === "password" ? "text" : "password");
  };

  return (
    <section>
      <div className="container-fluid">
        <div className="row align-items-stretch">
          <div className="col-lg-6 bg-gray-lightest">
            <div className="row min-vh-100 align-items-center justify-content-center">
              <div className="col-xxl-7 col-lg-10 col-md-8 col-10 pt-md-2 pt-1 pb-md-1 pb-1">
                <div className="text-center mb-2">
                  {loginSettings?.logo && (
                    <Link to="/" className="text-decoration-none">
                      <img
                        src={loginSettings?.logo}
                        alt="logo"
                        className="auth-logo"
                      />
                    </Link>
                  )}
                </div>
                <form onSubmit={handleLogin}>
                  <div className="btn-group w-100 mb-3" role="group" aria-label="Giriş tipi">
                    <button
                      type="button"
                      className={`btn w-50 ${loginType === "username" ? "btn-blue" : "btn-outline-secondary"} rounded-start-3`}
                      onClick={() => {
                        setLoginType("username");
                        setUsername("");
                      }}
                    >
                      <i className="fas fa-user me-2"></i>Kullanıcı Adı
                    </button>
                    <button
                      type="button"
                      className={`btn w-50 ${loginType === "phone" ? "btn-blue" : "btn-outline-secondary"} rounded-end-3`}
                      onClick={() => {
                        setLoginType("phone");
                        setUsername("");
                      }}
                    >
                      <i className="fas fa-phone-alt me-2"></i>Telefon
                    </button>
                  </div>
                  {loginType === "phone" ? (
                    <div className="form-floating shadow-lg mb-3 rounded-3">
                      <InputMask
                        mask="05(99) 999 99 99"
                        maskChar="_"
                        value={username}
                        onChange={(e) => {
                          const digits = e.target.value.replace(/\D/g, "");
                          setUsername(digits);
                        }}
                      >
                        {(inputProps) => (
                          <input
                            {...inputProps}
                            type="text"
                            className="form-control border-0 rounded-3 px-4"
                            placeholder="05(__) ___ __ __"
                            inputMode="numeric"
                          />
                        )}
                      </InputMask>
                      <label htmlFor="loginUsername" className="px-4">
                        <i className="fas fa-phone-alt me-3"></i>Telefon Numarası
                      </label>
                    </div>
                  ) : (
                    <div className="form-floating shadow-lg mb-3 rounded-3">
                      <input
                        type="text"
                        className="form-control border-0 rounded-3 px-4"
                        placeholder="Kullanıcı Adı"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                      />
                      <label htmlFor="loginUsername" className="px-4">
                        <i className="fas fa-user me-3"></i>Kullanıcı Adı
                      </label>
                    </div>
                  )}
                  <div className="form-floating shadow-lg mb-2 rounded-3">
                    <input
                      type={passwordType}
                      className="form-control border-0 rounded-3 px-4"
                      id="loginPassword"
                      placeholder="Şifre"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                    <label htmlFor="loginPassword" className="px-4">
                      <i className="fas fa-lock me-3"></i>Şifre
                    </label>
                    {password?.length > 0 && (
                      <>
                        <FaEye
                          className={
                            passwordType === "password"
                              ? "d-none password-eye"
                              : "password-eye"
                          }
                          onClick={handlePasswordTypeChange}
                        />
                        <FaEyeSlash
                          className={
                            passwordType === "text"
                              ? "d-none password-eye-slash"
                              : "password-eye-slash"
                          }
                          onClick={handlePasswordTypeChange}
                        />
                      </>
                    )}
                  </div>
                  <div className="text-end mb-2">
                    <Link to={{ pathname: "/passwordReset" }}>
                      Şifremi Unuttum
                    </Link>
                  </div>
                  <button
                    type="submit"
                    className="btn btn-blue py-sm-3 py-2 px-sm-4 px-3 rounded-3 w-100 mb-2"
                    disabled={disabledButton || loading}
                  >
                    {loading && (
                      <span className="spinner-border spinner-border-sm me-3"></span>
                    )}
                    Giriş Yap <i className="fas fa-arrow-right ms-3"></i>
                  </button>
                  <div className="text-center text-gray small">
                    Hesabınız yok mu?{" "}
                    <Link to={{ pathname: "/signUp" }}>Yeni Üye Ol</Link>
                  </div>
                </form>

                <div className="text-center mt-md-2 mt-2">
                  <p>
                    <a
                      href="tel:08503351358"
                      className="text-gray-darkest text-decoration-none"
                    >
                      <i className="fas fa-phone-alt me-3"></i>0850 335 1358
                    </a>
                  </p>
                  <span className="fw-lighter opacity-50">
                    Copyright © 2024 Jarvis. Tüm Hakları Saklıdır.
                  </span>
                  <p className="small mb-0 opacity-50 d-flex justify-content-center flex-nowrap">
                    <button
                      type="button"
                      className="text-decoration-none text-reset border-0 bg-transparent"
                      style={{
                        fontSize: "12px",
                        fontWeight: "bold",
                        whiteSpace: "nowrap",
                        paddingRight: 2,
                      }}
                      onClick={() => setShowAydinlatmaModal(true)}
                    >
                      Aydınlatma Metni
                    </button>
                    <span style={{ fontSize: "12px" }}>|</span>
                    <button
                      type="button"
                      className="text-decoration-none text-reset border-0 bg-transparent"
                      style={{
                        fontSize: "12px",
                        fontWeight: "bold",
                        whiteSpace: "nowrap",
                        paddingLeft: 2,
                        paddingRight: 2,
                      }}
                      onClick={() => setShowCerezPolitikasiModal(true)}
                    >
                      Çerez Politikası
                    </button>
                    <span style={{ fontSize: "12px" }}>|</span>
                    <button
                      type="button"
                      className="text-decoration-none text-reset border-0 bg-transparent"
                      style={{
                        fontSize: "12px",
                        fontWeight: "bold",
                        whiteSpace: "nowrap",
                        paddingLeft: 2,
                      }}
                      onClick={() => setShowKisiselVerilerModal(true)}
                    >
                      Kişisel Veriler Bildirimi
                    </button>
                  </p>
                </div>
              </div>
            </div>
          </div>
          {(() => {
            const hasLoginText =
              loginSettings?.loginPageTitle || loginSettings?.loginPageDescription;

            // Yazı yoksa banner sağ tarafı tamamen kaplasın
            if (!hasLoginText) {
              return (
                <div className="col-lg-6 d-lg-block d-none bg-blue p-0">
                  {loginSettings?.banner && (
                    <img
                      src={loginSettings?.banner}
                      alt=""
                      className="w-100"
                      style={{
                        objectFit: "cover",
                        height: "100vh",
                        display: "block",
                      }}
                    />
                  )}
                </div>
              );
            }

            return (
              <div className="col-lg-6 d-lg-block d-none bg-blue">
                <div className="row min-vh-100 align-items-center justify-content-center">
                  <div className="col-xxl-8 col-lg-10 py-md-8 py-5">
                    <div className="swiper sliderLogin">
                      <div className="swiper-wrapper mb-md-8 mb-5">
                        {loginSettings?.banner && (
                          <div className="bg-none">
                            <div className="text-white">
                              <img
                                src={loginSettings?.banner}
                                alt=""
                                className="login-banner mb-5"
                              />

                              {loginSettings?.loginPageTitle && (
                                <h2 className="text-white mb-3">
                                  {loginSettings?.loginPageTitle}
                                </h2>
                              )}

                              {loginSettings?.loginPageDescription && (
                                <p className="mb-4">
                                  {loginSettings?.loginPageDescription}
                                </p>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="swiper-pagination swiper-pagination-white"></div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      </div>

      {showConfirmModal && (
        <div
          className="modal fade show"
          id="confirmModal"
          tabIndex="-1"
          aria-labelledby="confirmModalLabel"
          aria-hidden="true"
          style={{ display: "block", backgroundColor: "rgba(0, 0, 0, 0.5)" }}
          data-bs-backdrop="static"
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" id="confirmModalLabel">
                  Hesabınızı doğrulayın
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                  onClick={() => setShowConfirmModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <p className="text-center">
                  Kalan Süre:&nbsp;
                  <span style={{ fontWeight: 600 }}>{formatTime(timer)}</span>
                </p>
                <p>
                  Hesabınızı doğrulamak için telefon numaranıza{" "}
                  <strong>SMS</strong> ile gönderilen doğrulama kodunu giriniz.
                  {confirmMobileNo && (
                    <>
                      <br />
                      <strong>{maskPhone(confirmMobileNo)}</strong>
                    </>
                  )}
                </p>
                {timerExpired && (
                  <p className="text-center text-danger">
                    Süreniz doldu. Lütfen "Tekrar Kod Gönder" butonuna basarak
                    yeni kod isteyiniz.
                  </p>
                )}
                <div className="form-floating shadow-lg mb-4 rounded-3">
                  <input
                    type="text"
                    className="form-control border-0 rounded-3 px-4"
                    placeholder="Doğrulama Kodu"
                    value={confirmCode}
                    onChange={(e) => setConfirmCode(e.target.value)}
                    disabled={timerExpired}
                  />
                  <label htmlFor="loginConfirmCode" className="px-4">
                    <i className="fas fa-user me-3"></i>Doğrulama Kodu
                  </label>
                </div>
              </div>
              <div className="modal-footer">
                {timerExpired ? (
                  <button
                    type="button"
                    className="w-100 btn btn-blue py-2 px-sm-4 px-3 rounded-3"
                    onClick={handleResendCode}
                    disabled={!confirmMobileNo}
                  >
                    Tekrar Kod Gönder <i className="fas fa-redo ms-3"></i>
                  </button>
                ) : (
                  <button
                    type="button"
                    className="w-100 btn btn-blue py-2 px-sm-4 px-3 rounded-3"
                    onClick={handleConfirmCode}
                    disabled={!confirmMobileNo || !confirmCode}
                  >
                    Gönder <i className="fas fa-arrow-right ms-3"></i>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Aydınlatma Metni Modal */}
      <Modal
        show={showAydinlatmaModal}
        onHide={() => setShowAydinlatmaModal(false)}
        size="lg"
      >
        <Modal.Header>
          <Modal.Title>Aydınlatma Metni</Modal.Title>
          <button
            type="button"
            className="border-0 bg-transparent"
            onClick={() => setShowAydinlatmaModal(false)}
          >
            <i className="fas fa-times"></i>
          </button>
        </Modal.Header>
        <Modal.Body>
          <div
            className="agreement-content"
            style={{ maxHeight: "400px", overflowY: "auto" }}
          >
            <h5>
              6698 SAYILI KİŞİSEL VERİLERİN KORUNMASI KANUNU KAPSAMINDA
              AYDINLATMA METNİ
            </h5>
            <p>
              Jarvis Yazılım ve Danışmanlık A.Ş. ("Jarvis" olarak
              adlandırılacaktır) olarak özel hayatın gizliliğine saygı duyuyor
              ve önem veriyoruz. Bu nedenle, sizleri 6698 sayılı Kişisel
              Verilerin Korunması Kanunu (Bundan sonra "KVKK" olarak
              anılacaktır.) kapsamında kişisel verilerinizin kullanımı ve
              korunması ile ilgili haklarınız konusunda bilgilendirmek
              istiyoruz.
            </p>
            <h6>a. Veri Sorumlusu</h6>
            <p>
              Jarvis olarak jarvis.com.tr adresini ziyaretiniz süresince kişisel
              verilerinizi, 6698 sayılı Kişisel Verilerin Korunması Kanunu ve
              ilgili sair mevzuat kapsamında, veri sorumlusu sıfatı ile
              işlediğimizi tarafınıza bildiriyoruz.
            </p>
            <h6>Kişisel Verilerin Hangi Amaçla İşleneceği ve Hukuki Sebebi</h6>
            <p>
              Web sitemizi ziyaretiniz esnasında Google Analytics 'in çerez
              çözümleri vasıtasıyla cihazınızın tanımlanması ve ziyaret
              deneyiminizi iyileştirmek amacıyla çerez verileriniz
              işlenmektedir. Bu faaliyet süresince KVKK'nın 5 (2) maddesinde yer
              alan "f) İlgili kişinin temel hak ve özgürlüklerine zarar vermemek
              kaydıyla, veri sorumlusunun meşru menfaatleri için veri
              işlenmesinin zorunlu olması" hukuki sebebine dayanırız.
            </p>
            <p>Kişisel verileriniz,</p>
            <ul>
              <li>Ürün ve hizmetlerin satış süreçlerinin yürütülmesi,</li>
              <li>İletişim faaliyetlerinin yürütülmesi,</li>
              <li>Ürün ve hizmetlerimiz hakkında sizleri bilgilendirmek,</li>
              <li>Kurum yapı ve politikamızı sizlere tanıtmak,</li>
            </ul>
            <p>
              amaçlarıyla KVKK'nın 5 (2) maddesinde belirtilen "c) Bir
              sözleşmenin kurulması veya ifasıyla doğrudan doğruya ilgili olması
              kaydıyla, sözleşmenin taraflarına ait kişisel verilerin
              işlenmesinin gerekli olması", "f) İlgili kişinin temel hak ve
              özgürlüklerine zarar vermemek kaydıyla, veri sorumlusunun meşru
              menfaatleri için veri işlenmesinin zorunlu olması" hukuki
              sebepleri dahilinde işlenecektir.
            </p>
            <p>
              Kişisel verileriniz, Ticari elektronik ileti gönderimi kapsamında
              Ürün / Hizmetlerin tanıtımı, pazarlama süreçlerinin yürütülmesi
              amacıyla KVKK 5 (1) maddesinde belirtildiği üzere açık rızanız
              alınarak işlenecektir.
            </p>
            <h6>
              c. İşlenen Kişisel Verilerin Kimlere ve Hangi Amaçla
              Aktarılabileceği
            </h6>
            <p>
              Gerekmesi veya talep edilmesi halinde, kişisel verileriniz,
              KVKK'nın 8 (2) (a) maddesi uyarınca ilgili kişinin açık rızası
              aranmaksızın, ilgili mevzuat uyarınca yasal yükümlülüklerin yerine
              getirebilmesi amacıyla ilgili kamu kurum ve kuruluşlarıyla; ticari
              faaliyetlerimizin yürütülmesi amacıyla tedarikçilerimize
              aktarılabilecektir.
            </p>
            <h6>d. Kişisel Veri Toplamanın Yöntemi</h6>
            <p>
              Kişisel verileriniz, bu metnin (b) fıkrasında belirtilen amaçların
              yerine getirilebilmesi için otomatik veya otomatik olmayan
              yöntemlerle, ilgili kişi tarafından e-posta iletisi gönderilmesi,
              telefon araması ile iletişime geçilmesi ve internet sitemizin
              ziyaret edilmesi esnasında e-bülten üyeliği kısmına e-posta
              adresinizi bildirmeniz, iletişim formunu doldurarak göndermeniz
              veya çerez teknolojisiyle cihaz bilgilerinin analiz dahil edilmesi
              şeklinde elektronik ortamdan elde edilmektedir.
            </p>
            <h6>e. KVKK'nın 11. Maddesi Kapsamındaki Haklarınız</h6>
            <p>
              KVKK'nın 11. maddesi kapsamında, veri sahibi olarak haklarınız
              bulunmakta olup bu haklarınız ile ilgili taleplerinizi KVKK'nın
              11. maddesi ile 13. maddesinin 1. fıkrası ve Veri Sorumlusuna
              Başvuru Usul ve Esasları Hakkında Tebliğ gereğince,
            </p>
            <ul>
              <li>
                "Brandium Küçükbakkalköy Mah. Dereboyu Cad. R5 Blok D:46 Kat:7
                Ataşehir / Istanbul" adresindeki Şirketimize bizzat gelerek,
              </li>
              <li>
                info@jarvis.com.tr şeklindeki kayıtlı elektronik posta (KEP)
                adresimiz üzerinden,
              </li>
              <li>
                kimliğinizi tespit edebilmek ve yanlış kişilere bilgi vermemek
                adına yazılı olarak noter aracılığı ile veya iadeli taahhütlü
                mektup ile,
              </li>
              <li>
                güvenli elektronik imza, mobil imza ya da (varsa) tarafınızca
                daha Şirketimize bildirilen ve sistemlerimizde kayıtlı bulunan
                elektronik posta adresini kullanmak suretiyle info@jarvis.com.tr
                adresine elektronik posta göndererek veya gelecekte Kurul'un
                belirleyeceği diğer yöntemlerle iletebilirsiniz.
              </li>
            </ul>
            <h6>f. Kişisel Verilerin Saklanma Süresi</h6>
            <p>
              Jarvis, kişisel verilerin işleme amacının ortan kalkması, Kanunlar
              ve ilgili sair mevzuat kapsamında belirlenen zorunlu saklama
              sürelerinin dolmasıyla bilirlikte kişisel verileri siler, yok
              eder, imha eder veya anonim hale getirir.
            </p>
            <h6>g. Değişiklik ve Güncellemeler</h6>
            <p>
              İş bu aydınlatma metni, 6698 sayılı Kişisel Verilerin Korunması
              Kanunu ve ilgili sair mevzuat kapsamında hazırlanmıştır. İlgili
              yasal mevzuat ve/veya Jarvis kişisel veri işleme amaç ve
              politikalarında meydana gelecek değişiklikler doğrultusunda bahse
              konu aydınlatma metninde gerekli değişiklikler yapılabilir.
            </p>
          </div>
        </Modal.Body>
      </Modal>

      {/* Kişisel Veriler Hakkında Bildirim Modal */}
      <Modal
        show={showKisiselVerilerModal}
        onHide={() => setShowKisiselVerilerModal(false)}
        size="lg"
      >
        <Modal.Header>
          <Modal.Title>Kişisel Veriler Hakkında Bildirim</Modal.Title>
          <button
            type="button"
            className="border-0 bg-transparent"
            onClick={() => setShowKisiselVerilerModal(false)}
          >
            <i className="fas fa-times"></i>
          </button>
        </Modal.Header>
        <Modal.Body>
          <div
            className="agreement-content"
            style={{ maxHeight: "400px", overflowY: "auto" }}
          >
            <p>
              Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do
              eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut
              enim ad minim veniam, quis nostrud exercitation ullamco laboris
              nisi ut aliquip ex ea commodo consequat.
            </p>
            <p>
              Duis aute irure dolor in reprehenderit in voluptate velit esse
              cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat
              cupidatat non proident, sunt in culpa qui officia deserunt mollit
              anim id est laborum.
            </p>
          </div>
        </Modal.Body>
      </Modal>

      {/* Çerez Politikası Modal */}
      <Modal
        show={showCerezPolitikasiModal}
        onHide={() => setShowCerezPolitikasiModal(false)}
        size="lg"
      >
        <Modal.Header>
          <Modal.Title>Çerez Politikası</Modal.Title>
          <button
            type="button"
            className="border-0 bg-transparent"
            onClick={() => setShowCerezPolitikasiModal(false)}
          >
            <i className="fas fa-times"></i>
          </button>
        </Modal.Header>
        <Modal.Body>
          <div
            className="agreement-content"
            style={{ maxHeight: "400px", overflowY: "auto" }}
          >
            <h5>
              Jarvis.com.tr KULLANIMINDA KİŞİSEL VERİLERİN İŞLENMESİNE ve
              MAHREMİYETİNE DAİR AYDINLATMA BİLDİRİMİ
            </h5>

            <h6>1. Veri Sorumlusunun Kimliği</h6>
            <p>
              Jarvis Yazılım ve Danışmanlık AŞ / Ltd Şti konaklama ve yan
              hizmetlerinin sunulması amacıyla Jarvis.com.tr vasıtasıyla işlenen
              kişisel veriler bakımından veri sorumlusu sıfatıyla hareket eder.
            </p>

            <h6>2. Kişisel Verilerin Hangi Amaçla İşleneceği</h6>
            <p>
              Jarvis.com.tr kullanımı süresince kişisel veriler, kullanıcı
              bağlılığının sağlanması, işlem ve bilgi teknolojileri altyapı
              güvenliğinin sağlanması, sipariş ve satış durumlarında ticari
              kayıtların oluşturulması ve saklanması, talep ve şikayetlerin
              değerlendirilmesi ve işleme alınması amaçlarıyla sınırlı olarak
              işlenmektedir.
            </p>

            <h6>
              3. Kişisel Verilerin Kimlere Hangi Amaçlarla Aktarılabileceği
            </h6>
            <p>
              İşlenen kişisel verileriniz işlenme amacının yerine getirilmesi
              için gerektiği ölçüde Jarvis Yazılım ve Danışmanlık AŞ / Ltd Şti
              tüzel kişiliğinin destek aldığı tedarikçilerine ve iş ortaklarına
              aktarılır.
            </p>
            <p>
              Ayrıca resmi yazışmayla gerekli yasal/idari dayanak gösterilerek
              talep edilmesi ve Jarvis Yazılım ve Danışmanlık AŞ / Ltd Şti tüzel
              kişiliğinin diğer yasal yükümlüklerini yerine getirmesi gerektiği
              takdirde yetkili kamu kurum ve kuruluşlarına aktarılır.
            </p>
            <p>
              Jarvis.com.tr yerli bir program olup kullanımı süresince kişisel
              verileriniz yurt içinde güvenli ortamlarda işbu aydınlatma
              metninde sayılan amaçlarla sınırlı olarak kullanılmakta ve
              saklanmaktadır.
            </p>

            <h6>4. Kişisel Veri Toplamanın Yöntemi ve Hukuki Sebebi</h6>
            <p>
              Kişisel verileriniz Jarvis.com.tr programına bizimle kurduğunuz
              ilişki dolayısıyla tuttuğumuz kayıtlar esas alınarak doğrudan
              tüzel kişiliğimiz tarafından veya sizin tarafınızdan eklenir,
              işlem güvenliği ile ilgili kayıtlar program kullanımına bağlı
              olarak tutulmaktadır.
            </p>
            <p>
              Jarvis.com.tr programı kapsamında işlenen kişisel veriler
              sözleşmenin doğrudan ifası ve bir hakkın tesisi, kullanımı ve
              korunması hukuki sebepleriyle işlenir. Program kullanımı süresince
              kullanıcılara ait özel nitelikli kişisel veriler işlenmez.
            </p>

            <h6>5. İlgili Kişinin Hakları</h6>
            <p>
              İlgili kişiler 6698 Sayılı Kanun'un 11'inci maddesine yer alan
              haklarıyla ilgili taleplerini Jarvis Yazılım ve Danışmanlık AŞ /
              Ltd Şti 'ye iletebilirler.
            </p>
          </div>
        </Modal.Body>
      </Modal>
    </section>
  );
};

export default Login;
